import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, LinearProgress, Modal, Paper, Typography } from "@mui/material";
import { MeasureDataGroup, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMeasureDrawer } from "hooks/debt-hooks/use-measure-drawer";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useCreateManyMeasureDataSelect, useDateFormatSelect, useDealDefaultCurrencySelect } from "store";
import { useDealActions, useDebtActions } from "store/actions-context";
import { useDealIdSelector } from "store/selectors/deal-selectors/deal-selectors";
import { CurrencyType, DebtSegment, MeasureDataDraft } from "store/types";
import { defaultFacilityValues, getEndOfPeriod } from "utils/debt";

import { FacilityTermsDrawerValues } from "../FacilityTermsDrawerValues";
import { FormValues } from "../schemas";
import { STEP_META } from "./constants";
import { Step0EditChoice } from "./steps/Step0EditChoice";
import { Step1DebtConfig } from "./steps/Step1DebtConfig";
import { Step2PrincipalSetup } from "./steps/Step2PrincipalSetup";
import { Step3InterestType } from "./steps/Step3InterestType";
import { Step4InterestTerms } from "./steps/Step4InterestTerms";
import { Step5PIKTerms } from "./steps/Step5PIKTerms";
import { Step6Fees } from "./steps/Step6Fees";
import { Step7Review } from "./steps/Step7Review";
import { Step8Drawdown } from "./steps/Step8Drawdown";
import { Step9DrawdownReview } from "./steps/Step9DrawdownReview";
import { Step10Done } from "./steps/Step10Done";
import { EditChoice, FacilityCreationWizardProps, IndexField, InterestType, WithdrawalValues } from "./types";

// ── Main component ────────────────────────────────────────────────────────────

export const FacilityCreationWizard: React.FC<FacilityCreationWizardProps> = ({
  open,
  onClose,
  onApply,
  onCreateWithdrawal,
  loading,
  editMode = false,
  existingSegments = [],
  existingFees,
  existingWithdrawals = []
}) => {
  const { t } = useTranslation("debt");
  const currency = (useDealDefaultCurrencySelect() ?? "USD") as CurrencyType;
  const dateFormat = useDateFormatSelect();
  const { getDebtIndexValueAction } = useDebtActions();
  const dealId = useDealIdSelector() ?? 0;
  const { createManyMeasureDataAction, saveMeasureDataDraftAction } = useDealActions();
  const { action: createManyMeasureData, loading: measureDataLoading } = useActionHook(createManyMeasureDataAction);
  const createMeasureDataMeta = useCreateManyMeasureDataSelect();

  // ── Wizard-level state ────────────────────────────────────────────────────

  const [step, setStep] = useState(editMode ? 0 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [facilityCreated, setFacilityCreated] = useState(false);
  const [serverError, setServerError] = useState<string[] | null>(null);
  const [wizardSegments, setWizardSegments] = useState<FormValues[] | null>(null);

  const { rounds, roundsLoading } = useMeasureDrawer([
    dealId,
    ...(wizardSegments ?? []).map((segment) => segment.deal_id).filter(Boolean)
  ]);

  // Step 0 — explicit "debt type" choice
  const [debtTypeChosen, setDebtTypeChosen] = useState(false);

  // Step 2 — which interest types apply
  const [interestType, setInterestType] = useState<InterestType | null>(null);

  // Step 1 — whether principal is index-linked
  const [principalLinked, setPrincipalLinked] = useState<boolean | null>(null);

  // Step 4 — whether there are fees
  const [hasFees, setHasFees] = useState<boolean | null>(null);

  // Sequential reveal tracker — tracks which fields have been touched to progressively show more questions
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set());
  const reveal = useCallback(
    (name: string) =>
      setRevealedFields((prev) => {
        if (prev.has(name)) return prev;
        const next = new Set(prev);
        next.add(name);
        return next;
      }),
    []
  );

  // Edit-mode state
  const [editChoice, setEditChoice] = useState<EditChoice | null>(null);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState<number | null>(null);
  const [selectedWithdrawalIdx, setSelectedWithdrawalIdx] = useState<number | null>(null);
  const [newTermDate, setNewTermDate] = useState<string | null>(null);

  // Withdrawal data
  const [withdrawalValues, setWithdrawalValues] = useState<WithdrawalValues>({
    deal_title: "",
    loan_amount: 0,
    start_date: dayjs().format(),
    loan_first_status_date: null,
    principal_first_payment_date: null,
    interest_first_payment_date: null,
    pik_first_capitalization_date: null,
    loan_reset_date: null
  });

  // Main formik instance
  const formik = useFormik<FormValues>({
    initialValues: { ...defaultFacilityValues },
    onSubmit: () => {}
  });
  const { values, setFieldValue } = formik;

  // ── Reset on open/close ───────────────────────────────────────────────────

  useEffect(() => {
    formik.resetForm({ values: { ...defaultFacilityValues } });
    setStep(editMode ? 0 : 1);
    setSubmitting(false);
    setFacilityCreated(false);
    setDebtTypeChosen(false);
    setInterestType(null);
    setPrincipalLinked(null);
    setHasFees(null);
    setRevealedFields(new Set());
    setWizardSegments(null);
    setEditChoice(null);
    setSelectedSegmentIdx(null);
    setSelectedWithdrawalIdx(null);
    setNewTermDate(null);
    setWithdrawalValues({
      deal_title: "",
      loan_amount: 0,
      start_date: dayjs().format(),
      loan_first_status_date: null,
      principal_first_payment_date: null,
      interest_first_payment_date: null,
      pik_first_capitalization_date: null,
      loan_reset_date: null
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation ────────────────────────────────────────────────────────────

  const hasRegularInterest = ["regular", "both"].includes(interestType ?? "");
  const hasPIK = ["pik", "both"].includes(interestType ?? "");
  const hasPrincipalSchedule = ["fixed_principal", "fixed_total"].includes(values.payment_term);
  const isSimpleLoan = values.is_withdrawal;

  const activeSteps = useMemo(() => {
    if (editChoice === "edit_drawdown") {
      return [0, 8];
    } else if (editChoice === "add_drawdown") {
      return [0, 8, 9, 10];
    } else if (editMode) {
      return [
        0,
        1,
        ...(hasPrincipalSchedule ? [2] : []),
        3,
        ...(hasRegularInterest ? [4] : []),
        ...(hasPIK ? [5] : []),
        7
      ];
    } else {
      return [
        1,
        ...(hasPrincipalSchedule ? [2] : []),
        3,
        ...(hasRegularInterest ? [4] : []),
        ...(hasPIK ? [5] : []),
        ...(values.is_facility ? [6] : []),
        7,
        ...(!isSimpleLoan && values.is_facility ? [8, 9] : []),
        10
      ];
    }
  }, [editMode, editChoice, hasPrincipalSchedule, hasRegularInterest, hasPIK, values.is_facility, isSimpleLoan]);

  const currentStepIndex = activeSteps.indexOf(step);
  const totalSteps = activeSteps.length;
  const progressPct = totalSteps > 1 ? (currentStepIndex / (totalSteps - 1)) * 100 : 0;

  // Group withdrawal segments by deal_id, sorted chronologically so [0] = first, [last] = current
  const groupedWithdrawals = useMemo(() => {
    const map = new Map<number, DebtSegment[]>();
    for (const seg of existingWithdrawals) {
      const existing = map.get(seg.deal_id) ?? [];
      map.set(seg.deal_id, [...existing, seg]);
    }
    return Array.from(map.values()).map((segs) => [...segs].sort((a, b) => (a.start_date < b.start_date ? -1 : 1)));
  }, [existingWithdrawals]);

  // Pre-populate form from an existing segment when entering the main wizard flow
  const populateFromSegment = useCallback(
    (seg: DebtSegment, overrideStartDate?: string) => {
      formik.resetForm({
        values: {
          ...defaultFacilityValues,
          ...seg,
          ...(overrideStartDate ? { start_date: overrideStartDate } : {}),
          interest_index_value: null,
          principal_index_value: null,
          pik_index_value: null,
          custom_payments_file: null,
          fees: existingFees ?? []
        }
      });
      setDebtTypeChosen(true);
      const hasReg = !!seg.base_rate || seg.float_type === "spread";
      const hasPikV = !!seg.pik_base_rate || seg.pik_float_type === "spread";
      setInterestType(hasReg && hasPikV ? "both" : hasReg ? "regular" : hasPikV ? "pik" : "none");
      setPrincipalLinked(!!seg.principal_reference_index);
      setHasFees((existingFees ?? []).length > 0);
      setRevealedFields(
        new Set([
          "base_rate",
          "float_type",
          "interest_frequency",
          "pik_base_rate",
          "pik_float_type",
          "pik_cap_frequency"
        ])
      );
      // setInterestType() above only takes effect on the next render, so callers that need
      // hasReg/hasPikV in the same tick (e.g. goNext auto-filling withdrawal dates) can't
      // read the `hasRegularInterest`/`hasPIK` consts yet — return the freshly computed values instead.
      return { hasReg, hasPikV };
    },
    [existingFees] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const goNext = () => {
    const next = activeSteps[currentStepIndex + 1];
    if (next === undefined) return;

    // Pre-populate when entering the drawdown edit step
    if (editMode && step === 0 && next === 8 && editChoice === "edit_drawdown") {
      const group =
        groupedWithdrawals.length === 1 ? groupedWithdrawals[0] : groupedWithdrawals[selectedWithdrawalIdx ?? 0];
      if (group?.length) {
        const lastSeg = group[group.length - 1];
        populateFromSegment(lastSeg);
        setWithdrawalValues({
          deal_title: lastSeg.deal_title ?? "",
          loan_amount: lastSeg.loan_amount,
          start_date: lastSeg.start_date,
          loan_first_status_date: lastSeg.loan_first_status_date ?? null,
          principal_first_payment_date: lastSeg.principal_first_payment_date ?? null,
          interest_first_payment_date: lastSeg.interest_first_payment_date ?? null,
          pik_first_capitalization_date: lastSeg.pik_first_capitalization_date ?? null,
          loan_reset_date: lastSeg.loan_reset_date ?? null
        });
      }
    }

    // Pre-populate facility terms when adding a new drawdown
    if (editMode && step === 0 && next === 8 && editChoice === "add_drawdown") {
      const seg = existingSegments[existingSegments.length - 1];
      // Read frequencies straight off `seg` (not `values`/hasRegularInterest/hasPIK/hasPrincipalSchedule) —
      // those all reflect the form's state from *before* populateFromSegment's updates land.
      const { hasReg, hasPikV } = seg
        ? populateFromSegment(seg)
        : { hasReg: false, hasPikV: false };
      const start = dayjs();
      const hasPrincipal = !!seg && ["fixed_principal", "fixed_total"].includes(seg.payment_term);
      setWithdrawalValues({
        deal_title: "",
        loan_amount: 0,
        start_date: start.format(),
        loan_first_status_date:
          seg?.loan_status_frequency ? getEndOfPeriod(start, seg.loan_status_frequency) : null,
        principal_first_payment_date:
          hasPrincipal && seg?.principal_frequency ? getEndOfPeriod(start, seg.principal_frequency) : null,
        interest_first_payment_date:
          hasReg && seg?.interest_frequency ? getEndOfPeriod(start, seg.interest_frequency) : null,
        pik_first_capitalization_date:
          hasPikV && seg?.pik_capitalization_frequency
            ? getEndOfPeriod(start, seg.pik_capitalization_frequency)
            : null,
        loan_reset_date: seg?.loan_reset_frequency ? getEndOfPeriod(start, seg.loan_reset_frequency) : null
      });
    }

    // Pre-populate when crossing from edit step into main wizard
    if (editMode && step === 0 && next === 1 && existingSegments.length > 0) {
      const segIdx =
        editChoice === "change"
          ? existingSegments.length === 1
            ? 0
            : (selectedSegmentIdx ?? existingSegments.length - 1)
          : existingSegments.length - 1;
      const override = editChoice === "new_term" && newTermDate ? newTermDate : undefined;
      populateFromSegment(existingSegments[segIdx], override);
    }

    // Auto-fill first payment dates for simple loans
    if (isSimpleLoan && values.start_date) {
      const start = dayjs(values.start_date);
      if (next === 2 && hasPrincipalSchedule && !values.principal_first_payment_date)
        setFieldValue("principal_first_payment_date", getEndOfPeriod(start, values.principal_frequency));
      if (next === 4 && !values.interest_first_payment_date)
        setFieldValue("interest_first_payment_date", getEndOfPeriod(start, values.interest_frequency ?? "month"));
      if (next === 4 && values.loan_reset_frequency && !values.loan_reset_date)
        setFieldValue("loan_reset_date", getEndOfPeriod(start, values.loan_reset_frequency));
      if (next === 5 && values.pik_capitalization_frequency && !values.pik_first_capitalization_date)
        setFieldValue("pik_first_capitalization_date", getEndOfPeriod(start, values.pik_capitalization_frequency));
    }
    // Default drawdown date + auto-fill first payment dates when entering step 8 (creation only)
    if (next === 8 && !editMode && values.start_date) {
      const start = dayjs(values.start_date);
      setWithdrawalValues((prev) => ({
        ...prev,
        start_date: values.start_date,
        ...(values.loan_status_frequency
          ? { loan_first_status_date: getEndOfPeriod(start, values.loan_status_frequency) }
          : {}),
        ...(hasPrincipalSchedule && values.principal_frequency
          ? { principal_first_payment_date: getEndOfPeriod(start, values.principal_frequency) }
          : {}),
        ...(hasRegularInterest && values.interest_frequency
          ? { interest_first_payment_date: getEndOfPeriod(start, values.interest_frequency) }
          : {}),
        ...(hasPIK && values.pik_capitalization_frequency
          ? { pik_first_capitalization_date: getEndOfPeriod(start, values.pik_capitalization_frequency) }
          : {}),
        ...(values.loan_reset_frequency
          ? { loan_reset_date: getEndOfPeriod(start, values.loan_reset_frequency) }
          : {})
      }));
    }
    setStep(next);
  };

  const goBack = () => {
    const prev = activeSteps[currentStepIndex - 1];
    if (prev !== undefined) {
      setStep(prev);
      setServerError(null);
    }
  };

  // ── Index value fetching ──────────────────────────────────────────────────

  const fetchIndexValue = async (index: string, field: IndexField) => {
    try {
      const val = await getDebtIndexValueAction(index, dayjs().format("YYYY-MM-DD"));
      setFieldValue(field, val);
    } catch (_) {
      /* ignore */
    }
  };

  // ── Segment builders ──────────────────────────────────────────────────────

  const buildFacilitySegment = (): DebtSegment => {
    const {
      interest_index_value: _1,
      principal_index_value: _2,
      pik_index_value: _3,
      custom_payments_file: _4,
      fees: _5,
      ...rest
    } = values;

    const seg: DebtSegment = { ...rest, id: 0, deal_id: 0 };

    // In edit "change" mode: carry the existing segment's id so the backend updates it
    if (editMode && editChoice === "change") {
      const segIdx = existingSegments.length === 1 ? 0 : (selectedSegmentIdx ?? existingSegments.length - 1);
      const existing = existingSegments[segIdx];
      if (existing) {
        seg.id = existing.id;
        seg.deal_id = existing.deal_id;
      }
    }

    // In edit "new_term" mode: new segment, inherit deal_id from existing
    if (editMode && editChoice === "new_term") {
      seg.id = 0;
      if (existingSegments.length > 0) seg.deal_id = existingSegments[0].deal_id;
      if (newTermDate) seg.start_date = newTermDate;
    }

    if (interestType === "pik" || interestType === "none") {
      seg.base_rate = null;
      seg.float_type = "fixed";
      seg.interest_reference_index = null;
      seg.interest_first_payment_date = null;
    }
    if (interestType === "regular" || interestType === "none") {
      seg.pik_base_rate = null;
      seg.pik_float_type = "fixed";
      seg.pik_reference_index = null;
      seg.pik_first_capitalization_date = null;
    }

    return seg;
  };

  // ── Submit handlers ───────────────────────────────────────────────────────

  const extractErrors = (err: any): string[] => {
    const errors: Record<string, string[]> = err?.errors || {};
    const items = Object.values(errors).flat();
    return items.length ? items : [err?.message || "An unexpected error occurred"];
  };

  const handleFacilityConfirm = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const seg = buildFacilitySegment();
      const fees = hasFees ? values.fees : undefined;

      let segmentsToSend: DebtSegment[];
      if (editMode) {
        if (editChoice === "change") {
          const segIdx = existingSegments.length === 1 ? 0 : (selectedSegmentIdx ?? existingSegments.length - 1);
          segmentsToSend = existingSegments.map((s, i) => (i === segIdx ? seg : s));
        } else {
          // new_term: insert the new segment in chronological order
          segmentsToSend = [...existingSegments, seg].sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
        }
      } else {
        segmentsToSend = [seg];
      }

      await onApply(segmentsToSend, undefined, fees);
      if (editMode) {
        setFacilityCreated(true);
        return;
      }
      setFacilityCreated(true);
      if (isSimpleLoan) {
        setWizardSegments([{ ...values, deal_id: dealId }]);
      } else {
        goNext();
      }
    } catch (err) {
      const msgs = extractErrors(err);
      setServerError(msgs);
      toast.error(msgs.join(" "));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawalConfirm = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const withdrawalSeg: DebtSegment = {
        ...buildFacilitySegment(),
        id: 0,
        deal_id: 0,
        deal_title: withdrawalValues.deal_title,
        loan_amount: withdrawalValues.loan_amount,
        start_date: withdrawalValues.start_date,
        loan_first_status_date: withdrawalValues.loan_first_status_date,
        principal_first_payment_date: withdrawalValues.principal_first_payment_date,
        interest_first_payment_date: withdrawalValues.interest_first_payment_date,
        pik_first_capitalization_date: withdrawalValues.pik_first_capitalization_date,
        loan_reset_date: withdrawalValues.loan_reset_date,
        is_facility: false,
        is_withdrawal: true,
        overriding_parent: false
      };
      const result = await onCreateWithdrawal?.(withdrawalSeg);
      if (!result) return;
      setWizardSegments([{ ...values, ...withdrawalSeg, deal_id: result.data?.deal_id ?? 0 }]);
      goNext();
    } catch (err) {
      const msgs = extractErrors(err);
      setServerError(msgs);
      toast.error(msgs.join(" "));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDrawdownEdit = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const group =
        groupedWithdrawals.length === 1 ? groupedWithdrawals[0] : groupedWithdrawals[selectedWithdrawalIdx ?? 0];
      if (!group?.length) return;
      const firstSeg = group[0];
      const lastSeg = group[group.length - 1];
      const dateUpdates = {
        deal_title: withdrawalValues.deal_title,
        loan_amount: withdrawalValues.loan_amount,
        loan_first_status_date: withdrawalValues.loan_first_status_date,
        principal_first_payment_date: withdrawalValues.principal_first_payment_date,
        interest_first_payment_date: withdrawalValues.interest_first_payment_date,
        pik_first_capitalization_date: withdrawalValues.pik_first_capitalization_date,
        loan_reset_date: withdrawalValues.loan_reset_date
      };
      // All date updates including start_date go on the last segment
      const segments: DebtSegment[] =
        group.length === 1
          ? [{ ...firstSeg, ...dateUpdates, start_date: withdrawalValues.start_date }]
          : [firstSeg, { ...lastSeg, ...dateUpdates, start_date: withdrawalValues.start_date }];
      await onApply(segments, firstSeg.deal_id);
      onClose(true);
    } catch (err) {
      const msgs = extractErrors(err);
      setServerError(msgs);
      toast.error(msgs.join(" "));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMeasureData = async (data: {
    groups: MeasureDataGroup[];
    roundsByDeals: Record<number, MeasureDataRound[]>;
  }) => {
    await createManyMeasureData(data);
    setWizardSegments(null);
    goNext();
  };

  const handleSaveMeasureDataAsDraft = async (data: MeasureDataDraft["data"]) => {
    await saveMeasureDataDraftAction({
      deal_id: dealId,
      data,
      ...(createMeasureDataMeta?.draftId && { id: createMeasureDataMeta.draftId })
    });
  };

  // ── Footer logic ──────────────────────────────────────────────────────────

  const isLastStep = step === 10;
  const facilityAlreadyCreated = step === 7 && facilityCreated;

  const getNextLabel = (): string | null => {
    if (isLastStep) return null;
    if (step === 8 && editMode && editChoice === "edit_drawdown")
      return submitting ? t("wizard.common.autoCalculated") : t("wizard.drawdown.saveButton");

    if (step === 7) {
      if (facilityAlreadyCreated) return editMode ? "Done" : "Continue";
      return submitting ? "Saving…" : editMode ? "Save Changes" : isSimpleLoan ? "Create Loan" : "Create Facility";
    }
    if (step === 9) return submitting ? "Creating…" : "Create Drawdown";
    return "Continue";
  };

  const handleNextClick = () => {
    if (step === 9 && (!editMode || editChoice === "add_drawdown")) {
      handleWithdrawalConfirm();
    } else if (step === 8 && editMode && editChoice === "edit_drawdown") {
      handleDrawdownEdit();
    } else if (step === 7 && !facilityAlreadyCreated) {
      handleFacilityConfirm();
    } else if (step === 7 && facilityAlreadyCreated && editMode) {
      onClose(true);
    } else {
      goNext();
    }
  };

  const nextLabel = getNextLabel();
  const backDisabled = currentStepIndex === 0 || (facilityCreated && step === 7);

  const isNextDisabled = (() => {
    switch (step) {
      case 0: {
        if (!editChoice) return true;
        if (editChoice === "change" && existingSegments.length > 1 && selectedSegmentIdx === null) return true;
        if (editChoice === "new_term" && !newTermDate) return true;
        if (editChoice === "edit_drawdown" && groupedWithdrawals.length > 1 && selectedWithdrawalIdx === null)
          return true;
        return false;
      }
      case 1:
        return !debtTypeChosen || values.loan_amount <= 0 || !values.start_date || !values.maturity_date;
      case 2:
        return principalLinked === null || (principalLinked && !values.principal_reference_index);
      case 3:
        return interestType === null;
      case 4:
        return (
          (hasRegularInterest && values.base_rate == null) ||
          (hasRegularInterest && values.float_type !== "fixed" && !values.interest_reference_index) ||
          (hasRegularInterest && values.float_type === "spread" && !values.interest_rate_fixing) ||
          (hasRegularInterest && !values.interest_frequency)
        );
      case 5:
        return values.pik_base_rate == null || (values.pik_float_type !== "fixed" && !values.pik_reference_index);
      case 6:
        return hasFees === null;
      case 8:
        return withdrawalValues.loan_amount <= 0 || !withdrawalValues.start_date;
      default:
        return false;
    }
  })();

  const stepMeta = STEP_META[step] ?? { titleKey: "", subtitleKey: "" };

  // ── Render ────────────────────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <Step0EditChoice
            existingSegments={existingSegments}
            existingWithdrawals={existingWithdrawals}
            editChoice={editChoice}
            setEditChoice={(c) => {
              setEditChoice(c);
              setSelectedSegmentIdx(null);
              setSelectedWithdrawalIdx(null);
              setNewTermDate(null);
            }}
            selectedSegmentIdx={selectedSegmentIdx}
            setSelectedSegmentIdx={setSelectedSegmentIdx}
            selectedWithdrawalIdx={selectedWithdrawalIdx}
            setSelectedWithdrawalIdx={setSelectedWithdrawalIdx}
            newTermDate={newTermDate}
            setNewTermDate={setNewTermDate}
          />
        );
      case 1:
        return (
          <Step1DebtConfig
            formik={formik}
            dateFormat={dateFormat}
            currency={currency}
            isSimpleLoan={isSimpleLoan}
            debtTypeChosen={debtTypeChosen}
            setDebtTypeChosen={setDebtTypeChosen}
            reveal={reveal}
            editMode={editMode}
          />
        );
      case 2:
        return (
          <Step2PrincipalSetup
            formik={formik}
            dateFormat={dateFormat}
            isSimpleLoan={isSimpleLoan}
            principalLinked={principalLinked}
            setPrincipalLinked={setPrincipalLinked}
            fetchIndexValue={fetchIndexValue}
            reveal={reveal}
          />
        );
      case 3:
        return <Step3InterestType interestType={interestType} setInterestType={setInterestType} />;
      case 4:
        return (
          <Step4InterestTerms
            formik={formik}
            dateFormat={dateFormat}
            isSimpleLoan={isSimpleLoan}
            hasRegularInterest={hasRegularInterest}
            hasPIK={hasPIK}
            revealedFields={revealedFields}
            reveal={reveal}
            fetchIndexValue={fetchIndexValue}
          />
        );
      case 5:
        return (
          <Step5PIKTerms
            formik={formik}
            dateFormat={dateFormat}
            isSimpleLoan={isSimpleLoan}
            hasRegularInterest={hasRegularInterest}
            revealedFields={revealedFields}
            reveal={reveal}
            fetchIndexValue={fetchIndexValue}
          />
        );
      case 6:
        return <Step6Fees formik={formik} hasFees={hasFees} setHasFees={setHasFees} />;
      case 7:
        return (
          <Step7Review
            formik={formik}
            dateFormat={dateFormat}
            currency={currency}
            isSimpleLoan={isSimpleLoan}
            hasPrincipalSchedule={hasPrincipalSchedule}
            hasRegularInterest={hasRegularInterest}
            hasPIK={hasPIK}
            facilityCreated={facilityCreated}
            editMode={editMode}
            serverError={serverError}
          />
        );
      case 8:
        return (
          <Step8Drawdown
            formik={formik}
            dateFormat={dateFormat}
            hasPrincipalSchedule={hasPrincipalSchedule}
            hasRegularInterest={hasRegularInterest}
            hasPIK={hasPIK}
            withdrawalValues={withdrawalValues}
            setWithdrawalValues={setWithdrawalValues}
            currency={currency}
            isNewPeriod={
              editMode &&
              editChoice === "edit_drawdown" &&
              (groupedWithdrawals[selectedWithdrawalIdx ?? 0]?.length ?? 1) > 1
            }
          />
        );
      case 9:
        return (
          <Step9DrawdownReview
            formik={formik}
            dateFormat={dateFormat}
            currency={currency}
            withdrawalValues={withdrawalValues}
            serverError={serverError}
          />
        );
      case 10:
        return (
          <Step10Done
            isSimpleLoan={isSimpleLoan}
            isFacility={values.is_facility}
            editMode={editMode && editChoice !== "add_drawdown"}
            onClose={onClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal open={open} onClose={() => {}} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Paper
          sx={{
            width: "min(680px, 94vw)",
            height: "min(780px, 92vh)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 4
          }}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <Box sx={{ px: 5, pt: 5, pb: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {step === 8 && editMode
                    ? editChoice === "edit_drawdown"
                      ? withdrawalValues.deal_title || t(stepMeta.titleKey)
                      : t("wizard.drawdown.newDrawdownTitle")
                    : t(stepMeta.titleKey)}
                </Typography>
                {stepMeta.subtitleKey && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(stepMeta.subtitleKey)}
                  </Typography>
                )}
              </Box>
              {!isLastStep && (
                <IconButton onClick={() => onClose(facilityCreated)} sx={{ mt: -0.5 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>

            {/* Progress bar */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Step {currentStepIndex + 1} of {totalSteps}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(progressPct)}% complete
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progressPct} sx={{ height: 5, borderRadius: 3 }} />
            </Box>
          </Box>

          {/* ── Scrollable content ──────────────────────────────────────── */}
          <Box sx={{ flex: 1, overflow: "auto", px: 5, py: 4 }}>{renderStepContent()}</Box>

          {/* ── Footer ─────────────────────────────────────────────────── */}
          {!isLastStep && (
            <Box
              sx={{
                px: 5,
                py: 3,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              {!backDisabled ? (
                <Button startIcon={<ArrowBackIcon />} onClick={goBack} variant="text" color="inherit">
                  Back
                </Button>
              ) : (
                <Box />
              )}

              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                {step === 8 && !editMode && (
                  <Button variant="text" color="inherit" onClick={() => setStep(10)}>
                    Skip for now
                  </Button>
                )}
                {nextLabel && (
                  <Button
                    variant="contained"
                    onClick={handleNextClick}
                    disabled={submitting || loading || isNextDisabled}
                    sx={{ minWidth: 160 }}
                  >
                    {nextLabel}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Modal>

      {rounds && wizardSegments && (
        <FacilityTermsDrawerValues
          open={!!wizardSegments}
          rounds={rounds}
          loading={measureDataLoading || roundsLoading}
          segments={wizardSegments}
          onClose={() => {
            setWizardSegments(null);
            goNext();
          }}
          onSave={handleSaveMeasureData}
          onSaveAsDraft={handleSaveMeasureDataAsDraft}
        />
      )}
    </>
  );
};
